// src/pages/MyAdsPage.js
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { db, auth } from '../../firebase';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiCheckCircle, FiTrash2, FiRefreshCw, FiPlus, FiBox, FiStar, FiMoreVertical, FiZap } from 'react-icons/fi';
import { deleteAdPermanently, markAdAsSold, republishAd, featureTheAd, boostTheAd } from '../../services/AdManagement';
import { getUserProfile, } from '../../services/ProfileService';
import FeaturedCreditsTooltip from '../../components/FeaturedCreditsTooltip/FeaturedCreditsTooltip';
import './Ads.css'; // Using global styles for cards
import BoostCreditsTooltip from '../../components/BoostCreditsTooltip/BoostCreditsTooltip';

const MyAdsSkeleton = () => (
    <div className="my-ads-container my-ads-skeleton-page">
        <div className="dashboard-header">
            <div className="skeleton-line skeleton-title" />
            <div className="skeleton-line skeleton-subtitle" />
        </div>

        <div className="ads-list-vertical">
            {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="my-ad-card my-ad-card-skeleton">
                    <div className="ad-card-main">
                        <div className="skeleton-box skeleton-thumb" />
                        <div className="ad-card-info skeleton-info">
                            <div className="skeleton-line skeleton-badge" />
                            <div className="skeleton-line skeleton-heading" />
                            <div className="skeleton-line skeleton-price" />
                            <div className="skeleton-line skeleton-date" />
                        </div>
                    </div>
                    <div className="ad-card-actions">
                        <div className="skeleton-box skeleton-action" />
                        <div className="skeleton-box skeleton-action" />
                        <div className="skeleton-box skeleton-action" />
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const MyAdsPage = () => {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [promotingAdId, setPromotingAdId] = useState(null);
    const [boostingAdId, setBoostingAdId] = useState(null);
    const [confirmation, setConfirmation] = useState(null);
    const [openActionMenuId, setOpenActionMenuId] = useState(null);
    const actionMenuRef = useRef(null);
    const navigate = useNavigate();
    const user = auth.currentUser;

    const handleFeatureAd = (adId) => {
        featureTheAd(user.uid, adId).then(res => {
            if (res.success) window.location.reload();
        }).finally(() => {
            setPromotingAdId(null);
        });
    };

    const handleBoostAd = (adId) => {
        boostTheAd(user.uid, adId).then(res => {
            if (res.success) window.location.reload();
        }).finally(() => {
            setBoostingAdId(null);
        });
    };

    const askConfirmation = (action, payload) => {
        setConfirmation({ action, ...payload });
    };

    const handleCancelConfirmation = () => {
        setConfirmation(null);
    };

    const handleConfirmAction = async () => {
        if (!confirmation) return;

        const { action, adId, photoUrls } = confirmation;
        setConfirmation(null);

        try {
            if (action === 'sold') {
                await markAdAsSold(adId, user.uid);
            } else if (action === 'delete') {
                await deleteAdPermanently(adId, photoUrls);
            } else if (action === 'republish') {
                await republishAd({ adId, userId: user.uid });
            }
        } catch (error) {
            console.error('Confirmation action failed:', error);
        }
    };

    const fetchProfile = async () => {
        try {
            const data = await getUserProfile(user.uid);
            setProfile(data);
        } catch (error) {
            console.error("Error fetching profile:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!user) {
            navigate('/');
            return;
        }

        // Real-time listener for current user's ads
        const q = query(
            collection(db, 'listings'),
            where('userId', '==', user.uid),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const nextAds = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(ad => ad.status !== 'deleted');
            setAds(nextAds);
            setLoading(false);
        });
        fetchProfile();
        return () => unsubscribe();
    }, [user, navigate]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setOpenActionMenuId(null);
            }
        };

        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, []);

    const isExpiredAd = (ad) => {
        const validTill = ad.validTill?.toDate ? ad.validTill.toDate() : null;
        return ad.status === 'expired' || (validTill ? Date.now() > validTill.getTime() : false);
    };

    const getStatusLabel = (ad) => {
        if (isExpiredAd(ad)) return 'EXPIRED';
        if (ad.status === 'sold') return 'SOLD';
        return 'ACTIVE';
    };

    const getPrimaryPhoto = (photos = []) => {
        if (!photos.length) return 'https://via.placeholder.com/100?text=No+Image';

        const firstPhoto = photos[0];
        if (typeof firstPhoto === 'string') return firstPhoto;
        return firstPhoto?.thumb || firstPhoto?.full || 'https://via.placeholder.com/100?text=No+Image';
    };

    const confirmationText = confirmation ? {
        title: confirmation.action === 'sold'
            ? 'Mark this item as sold?'
            : confirmation.action === 'delete'
                ? 'Delete this ad?'
                : 'Republish this ad?',
        message: confirmation.action === 'sold'
            ? 'It will no longer be visible to buyers.'
            : confirmation.action === 'delete'
                ? 'This cannot be undone.'
                : 'This will make your ad active again for the next 30 days. Do you want to continue?',
        confirmLabel: confirmation.action === 'sold'
            ? 'Yes, mark sold'
            : confirmation.action === 'delete'
                ? 'Yes, delete it'
                : 'Yes, republish it',
    } : null;

    const handleMarkSold = (adId) => {
        askConfirmation('sold', { adId });
    };

    const handleDelete = (adId, photoUrls) => {
        askConfirmation('delete', { adId, photoUrls });
    };

    const handleRepublish = (adId) => {
        askConfirmation('republish', { adId });
    };

    const handleCardView = (adId) => {
        navigate(`/item/${adId}`);
    };

    const handleCardKeyDown = (event, adId) => {
        if (event.target !== event.currentTarget) return;

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleCardView(adId);
        }
    };

    const handleActionClick = (event, action) => {
        event.stopPropagation();
        setOpenActionMenuId(null);
        action();
    };

    if (loading) return <MyAdsSkeleton />;

    const promotingAd = ads.find((ad) => ad.id === promotingAdId);
    const boostingAd = ads.find((ad) => ad.id === boostingAdId);

    return (
        <div className="my-ads-container">
            <div className="dashboard-header">
                <div className="dashboard-heading">
                    <h1>My Ads</h1>
                    <p>You have {ads.length} listings</p>
                </div>

                {/* <div className="featured-credits-badge">
                    <FiStar className="sparkle-icon" />
                    <div className="featured-credits-text">
                        <span>Featured Ad Credits: <strong>{profile?.benefitsOffered?.freeFeaturedAd?.creditsAvailable || 0}</strong></span>
                    </div>
                    <FeaturedCreditsTooltip />
                </div> */}

                <div className="user-credits-hub">
                    {/* Boost Credits Badge (Primary MVP Focus) */}
                    <div className="credits-badge boost-badge">
                        <FiZap className="zap-icon" />
                        <div className="credits-text">
                            <span>Free Boosts: <strong>{profile?.credits?.boosts?.available || 0}</strong></span>
                        </div>
                        <BoostCreditsTooltip />
                    </div>

                    {/* Featured Credits Badge (Only shows if they purchased/have some, or as locked) */}
                    {profile?.credits?.featured?.available > 0 && (
                        <div className="credits-badge feature-badge">
                            <FiStar className="sparkle-icon" />
                            <div className="credits-text">
                                <span>Featured Credits: <strong>{profile?.credits?.featured?.available || 0}</strong></span>
                            </div>
                            <FeaturedCreditsTooltip />
                        </div>
                    )}
                </div>
            </div>

            {ads.length === 0 ? (
                <div className="empty-dashboard-container">
                    <div className="empty-state-card">
                        <div className="empty-icon-circle">
                            <FiBox size={40} />
                        </div>
                        <h2>No ads posted yet</h2>
                        <p>
                            Selling on Qinto is fast and easy. Snap a few photos and
                            let our AI handle the rest!
                        </p>
                        <button className="qinto-btn-large" onClick={() => navigate('/postad')}>
                            <FiPlus /> Post Your First Ad
                        </button>
                    </div>
                </div>
            ) : (
                <div className="ads-list-vertical">
                    {ads.map((ad) => {
                        const isExpired = isExpiredAd(ad);
                        const statusLabel = getStatusLabel(ad).toLowerCase();

                        return (
                            <div
                                key={ad.id}
                                className={`my-ad-card my-ad-card-clickable ${ad.status === 'sold' ? 'sold-overlay' : ''} ${isExpired ? 'expired-overlay' : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleCardView(ad.id)}
                                onKeyDown={(event) => handleCardKeyDown(event, ad.id)}
                                aria-label={`View ${ad.title}`}
                            >
                                <div className="ad-card-main">
                                    <img
                                        src={getPrimaryPhoto(ad.photos)}
                                        alt="ad-thumbnail"
                                        className="ad-card-img"
                                        onError={(e) => { e.target.src = 'https://via.placeholder.com/100?text=No+Image'; }}
                                    />
                                    <div className="ad-card-info">
                                        <div className={`status-badge status-${statusLabel}`}>{getStatusLabel(ad)}</div>
                                        <h3>{ad.title}</h3>
                                        <p className="ad-card-price">&#8377;{ad.price.toLocaleString('en-IN')}</p>
                                        <p className="ad-card-date">Posted on {ad.createdAt?.toDate ? ad.createdAt.toDate().toLocaleDateString() : 'Recently'}</p>
                                        {ad.activeAt && (
                                            <p className="ad-card-date" style={{color: 'var(--qinto-burgundy)'}}>Last boosted on {ad.activeAt.toDate().toLocaleDateString()}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="ad-card-controls" onClick={(event) => event.stopPropagation()}>
                                    <div className="promotion-actions-container">
                                        {/* Option 1: Boost this Ad (Chronological bump to top) */}
                                        {profile?.credits?.boosts?.available > 0 && (
                                            <button
                                                className="btn-promote btn-boost"
                                                onClick={() => setBoostingAdId(ad.id)}
                                            >
                                                <FiZap className="zap-icon" />
                                                <span>Boost to Top</span>
                                            </button>
                                        )}

                                        {/* Option 2: Feature this Ad (7-day premium pin) */}
                                        {!ad.isFeatured && profile?.credits?.featured?.available > 0 && (
                                            <button
                                                className="btn-promote btn-feature"
                                                onClick={() => setPromotingAdId(ad.id)}
                                            >
                                                <FiStar className="sparkle-icon" />
                                                <span>Feature this Ad</span>
                                            </button>
                                        )}
                                    </div>

                                    <div
                                        className="ad-card-actions-menu"
                                        ref={openActionMenuId === ad.id ? actionMenuRef : null}
                                    >
                                        <button
                                            type="button"
                                            className="ad-card-menu-trigger"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setOpenActionMenuId((currentId) => currentId === ad.id ? null : ad.id);
                                            }}
                                            aria-label={`More actions for ${ad.title}`}
                                            aria-expanded={openActionMenuId === ad.id}
                                            title="More actions"
                                        >
                                            <FiMoreVertical />
                                        </button>

                                        <AnimatePresence>
                                            {openActionMenuId === ad.id && (
                                                <motion.div
                                                    className="ad-card-action-dropdown"
                                                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                                                    transition={{ duration: 0.16, ease: 'easeOut' }}
                                                >
                                                    {ad.status !== 'sold' && (
                                                        <button
                                                            type="button"
                                                            onClick={(event) => handleActionClick(event, () => navigate(`/edit-ad/${ad.id}`))}
                                                        >
                                                            <FiEdit2 /> Edit
                                                        </button>
                                                    )}

                                                    {isExpired && (
                                                        <button
                                                            type="button"
                                                            className="republish-btn"
                                                            onClick={(event) => handleActionClick(event, () => handleRepublish(ad.id))}
                                                        >
                                                            <FiRefreshCw /> Republish
                                                        </button>
                                                    )}

                                                    {ad.status !== 'sold' && !isExpired && (
                                                        <button
                                                            type="button"
                                                            className="sold-btn"
                                                            onClick={(event) => handleActionClick(event, () => handleMarkSold(ad.id))}
                                                        >
                                                            <FiCheckCircle /> Mark as Sold
                                                        </button>
                                                    )}

                                                    <button
                                                        type="button"
                                                        className="delete-btn"
                                                        onClick={(event) => handleActionClick(event, () => handleDelete(ad.id, ad.photos))}
                                                    >
                                                        <FiTrash2 /> Delete
                                                    </button>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>

                                {ad.isFeatured && <span className="featured-status">Featured until {ad.featuredUntil.toDate().toLocaleDateString()}</span>}

                            </div>
                        )
                    })}
                </div>
            )}

            {createPortal(
                <AnimatePresence>
                    {promotingAd && (
                        <div className="modal-overlay" onClick={() => setPromotingAdId(null)}>
                            <motion.div
                                className="subtle-dialog"
                                initial={{ opacity: 0, scale: 0.96, y: 14 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.96, y: 14 }}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className="dialog-icon"><FiStar /></div>
                                <h3>Feature this ad?</h3>
                                <p>This will use <strong>1 credit</strong>. Once made featured, it cannot be undone.</p>

                                <div className="dialog-actions">
                                    <button className="btn-cancel" onClick={() => setPromotingAdId(null)}>Not now</button>
                                    <button className="btn-confirm" onClick={() => handleFeatureAd(promotingAd.id)}>Yes, feature it</button>
                                </div>
                            </motion.div>
                        </div>
                    )}

                    {boostingAd && (
                        <div className="modal-overlay" onClick={() => setBoostingAdId(null)}>
                            <motion.div
                                className="subtle-dialog"
                                initial={{ opacity: 0, scale: 0.96, y: 14 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.96, y: 14 }}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className="dialog-icon"><FiZap /></div>
                                <h3>Boost this ad to the top?</h3>
                                <p>This will use <strong>1 boost</strong> and move your listing back to the top of the local feed.</p>

                                <div className="dialog-actions">
                                    <button className="btn-cancel" onClick={() => setBoostingAdId(null)}>Not now</button>
                                    <button className="btn-confirm" onClick={() => handleBoostAd(boostingAd.id)}>Yes, boost it</button>
                                </div>
                            </motion.div>
                        </div>
                    )}

                    {confirmation && (
                        <div className="modal-overlay" onClick={handleCancelConfirmation}>
                            <motion.div
                                className="subtle-dialog"
                                initial={{ opacity: 0, scale: 0.96, y: 14 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.96, y: 14 }}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className="dialog-icon">✨</div>
                                <h3>{confirmationText.title}</h3>
                                <p>{confirmationText.message}</p>

                                <div className="dialog-actions">
                                    <button className="btn-cancel" onClick={handleCancelConfirmation}>Not now</button>
                                    <button className="btn-confirm" onClick={handleConfirmAction}>{confirmationText.confirmLabel}</button>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
};

export default MyAdsPage;
